import "./index.css";
import Contact from "../Contact";
import TagItems from "./TagItems";
import TagManage from "./TagManage";
import Container from "./ui/Container";
import EditContact from "../Contact/Edit";
import EditSelection from "./EditSelection";
import ExpandedState from "./ui/ExpandedState";
import { getCustomerId } from "../../../Common";
import GuidelineCheckbox from "./GuidelineCheckbox";
import { shallowEqual, useSelector } from "react-redux";
import React, { memo, useEffect, useMemo, useState } from "react";
import GuidelinesSelectButton from "./ui/GuidelinesSelectButton";

import { SHOW_CONTACT, IS_IFRAMES, PATH_CUSTOMER_ID, HIDE_CONTACT_ADD_EDIT } from "MortgageGPT/constants";

import type {
  TypeTag,
  TypeGuideline,
  TypeUseAllPrograms,
} from "../../hooks/useAllPrograms";
import type {
  TypeUseContact,
  TypeLenderContactPerson,
} from "../../hooks/useContact";
import type { TypeGuidelineCheckboxProps } from "./GuidelineCheckbox";

export interface TypeGuidelineSelectProps extends Pick<TypeGuidelineCheckboxProps,"disabled" | 'onPreview'> {
  /**
   * @param isMobile 是否是移动设备
   */
  isMobile?: boolean;
  /**
   * @param programs 主要数据流及方法
   */
  programs: TypeUseAllPrograms;
  /**
   * @param contact 联系人及方法
   */
  contact: TypeUseContact;
  /**
   * @name onChange 用户选择 “guideline tag” 后，传递“guideline id数组
   * @param ids guideline ids
   */
  onChange?: (ids: Array<TypeGuideline["id"]>) => void;
}

/**
 * @name getInitData 获取 sessionStorage 中的 tag、guideline 初始值
 * @param key "tag" | "guideline"
 */
function getInitData(key: "tag" | "guideline") {
  let cache = [];
  try {
    cache = JSON.parse(sessionStorage.getItem(`scenario_${key}`));
  } catch (e) {}
  const IS_ARRAY = Array.isArray(cache);
  const keys = IS_ARRAY
    ? cache.filter((k) => ![null, "", undefined].includes(k))
    : [];
  if (key === "tag" && !IS_ARRAY) return [-1];
  return keys;
}

// lender展开状态 cache key
const KEY_LENDER_EXPAND = "scenario_lender_expand";

/**
 * @name GuidelineSelect Guideline 选择、管理
 * @description 包含 Tag 选择、Guideline 选择，数据从 后端API 获取
 */
const GuidelineSelect: React.FC<TypeGuidelineSelectProps> = ({
  contact,
  programs,
  isMobile,
  onChange,
  disabled,
  onPreview
}) => {
  const IS_LOGGED = programs.data.IS_LOGGED;
  const { MAP_LENDER_CONTACT } = contact.data;
  const { TAGS, MAP_TAGS, GUIDELINES, MAP_GUIDELINE_ID, LIST_GROUP_GUIDELINES } = programs.data;

  const USER_EMAIL = useSelector(
    (e: any) =>
      e?.subscriptionReducer?.currentInfo?.stripeCustomer?.customer?.email,
    shallowEqual,
  );

  const [expand, setExpand] = useState<Record<string, boolean>>(() => {
    try {
      const CACHE = JSON.parse(sessionStorage.getItem(KEY_LENDER_EXPAND));
      return {}.toString.call(CACHE) === "[object Object]" ? CACHE : {};
    } catch (e) {
      return {};
    }
  });

  const [tags, setTags] = useState<TypeTag["id"][]>(() => getInitData("tag"));
  const [guidelines, setGuidelines] = useState<TypeGuideline["id"][]>(() => getInitData('guideline'));

  const [lenderContact, setLenderContact] = useState<TypeLenderContactPerson>();

  const CUSTOMER_ID = useMemo(() => IS_IFRAMES ? PATH_CUSTOMER_ID : getCustomerId(), [])

  // “Save selection” 功能
  const [selection, setSelection] = useState<Array<TypeGuideline["id"]>>([]);

  // tag 变化时，需要关联更新 guideline
  function onTagChange(id: TypeTag["id"], mapTags = MAP_TAGS) {
    // 如果之前是是全选状态，清空一次（仿照旧的交互逻辑）
    if (tags[0] === -1) {
      tags.splice(0);
      guidelines.splice(0);
    }
    const i = tags.indexOf(id);
    if (~i) {
      tags.splice(i, 1);
      const delIds = mapTags[id].documents;
      const ids = tags
        .map((id) => mapTags?.[id]?.documents)
        .flat()
        .filter(Boolean);
      const guidelinesFilter = guidelines.filter((d) => !delIds.includes(d));
      setTags([...tags]);
      setGuidelines([...new Set([...ids, ...guidelinesFilter])]);
    } else {
      setTags([...tags, id]);
      setGuidelines([...new Set([...guidelines, ...mapTags[id].documents])]);
    }
  }

  // 该方法是针对 “All” tag标签单独实现的，这个tag不存在于数据库中，"全选" 也是单独的逻辑
  function onTagAllChange() {
    if (tags[0] === -1) {
      setTags([]);
      setGuidelines([]);
    } else {
      setTags([-1]);
    }
  }

  function onGuidelineChange(id: (typeof guidelines)[number]) {
    setGuidelines((v) => {
      const ids = v.includes(id) ? v.filter((v) => v !== id) : [...v, id];
      const SET = new Set(ids);
      setTags(
        TAGS?.filter((v) => v.documents?.every((d) => SET.has(d))).map(
          (v) => v.id,
        ),
      );
      return ids;
    });
  }

  function onGuidelineSave(ids?: typeof guidelines) {
    setSelection(ids);
  }

  async function onCloseSaveSelection(id: TypeTag["id"]) {
    setSelection([]);
    if (id) {
      const { MAP_TAGS } = await programs.runAsync();
      onTagChange(id, MAP_TAGS);
    }
  }

  function onEditContact(user?: TypeLenderContactPerson) {
    setLenderContact(user);
    user?.lender_id || contact.run();
  }

  const IS_EXPAND = useMemo(() => {
    if (!LIST_GROUP_GUIDELINES?.length) return true;
    return !Object.values(expand).filter(Boolean).length;
  }, [expand, LIST_GROUP_GUIDELINES]);

  function onExpand(id?: TypeGuideline["id"]) {
    let value: typeof expand;
    if (id) {
      value = { ...expand, [id]: !expand[id] };
    } else {
      value = IS_EXPAND
        ? Object.fromEntries(
            LIST_GROUP_GUIDELINES.map((v) => [v.lenderId, true]),
          )
        : {};
    }
    setExpand(value);
    sessionStorage.setItem(KEY_LENDER_EXPAND, JSON.stringify(value));
  }

  // 全选
  useEffect(() => {
    if (tags?.[0] === -1 && GUIDELINES?.length) {
       setGuidelines(GUIDELINES.map((v) => v.id));
    } 
  }, [tags, GUIDELINES, MAP_GUIDELINE_ID]);

  // 所选的 “guidelines” 一定得与后端权限绑定，如果没有权限就需要过滤掉
  useEffect(() => {
    if (!GUIDELINES?.length) return;
    setGuidelines((v) => v.filter((id) => MAP_GUIDELINE_ID?.[id]?.available));
  }, [GUIDELINES, MAP_GUIDELINE_ID]);

  // 操作 tag、guideline 都会触发 guidelines 状态变更，这里统一处理onChange事件
  useEffect(() => {
    onChange?.(guidelines);
    sessionStorage.setItem("scenario_guideline", JSON.stringify(guidelines));
  }, [guidelines]);

  useEffect(() => {
    sessionStorage.setItem("scenario_tag", JSON.stringify(tags));
  }, [tags]);

  // 初始化状态仅展开 第一个lender
  useEffect(() => {
    if (
      LIST_GROUP_GUIDELINES?.length &&
      !sessionStorage.getItem(KEY_LENDER_EXPAND)
    ) {
      setExpand({ [LIST_GROUP_GUIDELINES[0]?.lenderId]: true });
    }
  }, [LIST_GROUP_GUIDELINES]);

  // @Andrew Zhang 这三种客户他们都用的自己的guideline， 所以就相当于一个lender， 所以不需要有展开和收起的操作
  // 另外只有一个lender的时候也不需要展开/收起
  const SHOW_EXPAND_ICON = useMemo(() => {
    const ORG = ["greenbox", "calhfa", "nationdirect"]; // 受限制的 “Organization”
    const IS_SINGLE_LENDER = LIST_GROUP_GUIDELINES?.length === 1;
    return !ORG.includes(CUSTOMER_ID) && !IS_SINGLE_LENDER;
  }, [LIST_GROUP_GUIDELINES, CUSTOMER_ID]);

  const SHOW_SAVE_BTB = useMemo(() => {
    const EMAILS = ['test@nationwide.com']; // 该用户不得显示 “Save this selection” 按钮
    return IS_LOGGED && !EMAILS.includes(USER_EMAIL);
  }, [IS_LOGGED, USER_EMAIL]);

  // 是否可使用 “Contact” 编辑、新增功能
  const SHOW_CONTACT_AD = useMemo(() => {
    return IS_LOGGED ? !HIDE_CONTACT_ADD_EDIT.includes(CUSTOMER_ID) : false;
  }, [IS_LOGGED, CUSTOMER_ID]);

  return (
    <Container
      loading={programs.loading}
      extra={
        IS_LOGGED && <TagManage onDeleteTag={onTagChange} programs={programs} />
      }
    >
      <TagItems
        value={tags}
        isMobile={isMobile}
        programs={programs}
        onChange={onTagChange}
        onAllChange={onTagAllChange}
      />

      <div className="guidelines-header-tools">
        <GuidelinesSelectButton
          disabled={disabled}
          showBtn={SHOW_SAVE_BTB}
          count={guidelines?.length}
          onClick={() => onGuidelineSave(guidelines)}
        />
        {SHOW_EXPAND_ICON && <ExpandedState expand={IS_EXPAND} onClick={onExpand} />}
      </div>

      {LIST_GROUP_GUIDELINES?.map((v) => (
        <GuidelineCheckbox
          key={v.lenderId}
          value={guidelines}
          options={v.options}
          disabled={disabled}
          onPreview={onPreview}
          name={v.lenderDisplayName}
          expand={SHOW_EXPAND_ICON ? expand[v.lenderId] : true}
          onChange={onGuidelineChange}
          showExpand={SHOW_EXPAND_ICON}
          onExpand={() => onExpand(v.lenderId)}
        >
          {SHOW_CONTACT && <Contact
              lender_id={v.lenderId}
              lender_name={v.lenderDisplayName}
              list={MAP_LENDER_CONTACT?.[v?.lenderId]}
              onEdit={SHOW_CONTACT_AD ? onEditContact : undefined}
          />}
        </GuidelineCheckbox>
      ))}

      <EditSelection
        programs={programs}
        guidelineIds={selection}
        onClose={onCloseSaveSelection}
      />

      <EditContact {...lenderContact} onCancel={onEditContact} />
    </Container>
  );
};

export default memo(GuidelineSelect);
